import { takeLatest, call, put } from '@redux-saga/core/effects';
import Logger from '@noths/logger/build/client';
import queryString from 'query-string';

import Types from '../../types';
import { signIn } from '../../utils/user';
import { scrollToTopOfPage } from '../../utils/page';
import { authenticateUserWithNoths } from '../../../services/AuthenticateService';
import {
  trackSignInClick,
  trackSignInCredentialsInvalid,
  trackPasswordResetRequiredError,
  trackSignInSuccess,
  trackSignInUserDisabled,
} from '../../../analytics';
import getErrorMessage from '../../utils/getErrorMessage';
import { persistError } from '../../utils/errors';
import { ROUTES } from '../../../constants';
import { persist, getPersisted, removePersisted } from '../../utils/persist';

export function* signInSuccess(email) {
  try {
    yield call(authenticateUserWithNoths);
    yield put({ type: Types.MODAL_CHECK });
    yield call(trackSignInSuccess);
  } catch (error) {
    Logger.error(getErrorMessage(error), { extra: { description: 'Sign In Session' } });
    persistError('signIn', Types.SIGN_IN_SESSION_AUTH_ERROR);
    yield put({ type: Types.SIGN_OUT });
  }
}

export function signInRedirect() {
  const redirectUrl = getPersisted('loginRedirectUrl') || '/';
  removePersisted('loginRedirectUrl');

  window.location.assign(`//${window.location.host}${redirectUrl}`);
}

export function redirectToPasswordReset(email) {
  localStorage.setItem('resetRequiredEmail', email);
  window.location.assign(ROUTES.RESET_PASSWORD_REQUIRED);
}

export function persistRedirectUrl({ location: { search = '' } } = {}) {
  if (search) {
    const query = queryString.parse(search);
    if (query.redirect_url) {
      persist('loginRedirectUrl', query.redirect_url);
    }
  }
}

export function* signInFailure(error, email) {
  if (error.message === 'User is disabled') {
    yield call(trackSignInUserDisabled);
    yield put({ type: Types.SIGN_IN_USER_DISABLED_ERROR });
  } else if (error.code === 'NotAuthorizedException' || error.code === 'UserNotFoundException') {
    yield call(trackSignInCredentialsInvalid);
    yield put({ type: Types.SIGN_IN_CREDENTIALS_ERROR });
  } else if (error.code === 'PasswordResetRequiredException') {
    yield call(trackPasswordResetRequiredError);
    yield call(redirectToPasswordReset, email);
  } else {
    Logger.error('Sign In Error', { extra: { description: `Sign In`, cognitoError: error } });
    yield put({ type: Types.SIGN_IN_COGNITO_ERROR });
  }
  yield call(scrollToTopOfPage);
}

export function* signInSubmit({ email, password }) {
  try {
    yield call(trackSignInClick);
    yield call(signIn, email, password);
    yield call(signInSuccess, email);
  } catch (error) {
    yield call(signInFailure, error, email, password);
  }
}

export function* signInSaga() {
  yield takeLatest(Types.SIGN_IN_SHOW, persistRedirectUrl);
  yield takeLatest(Types.SIGN_IN_SUBMIT, signInSubmit);
  yield takeLatest(Types.SIGN_IN_MODAL_CLOSE, signInRedirect);
  yield takeLatest(Types.SIGN_IN_REDIRECT, signInRedirect);
}

export default signInSaga;
